import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  draftFromTestName,
  LabResultLinesEditor,
  linesToDraft,
  type LabResultLineDraft,
} from "@/components/laboratory/lab-result-lines-editor";
import { LabResultPrintSheet, printLabResult } from "@/components/laboratory/LabResultPrintSheet";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  LabOrderValidationError,
  listLabOrders,
  saveLabResult,
  syncLabOrdersFromApi,
  updateLabOrderStatus,
} from "@/core/laboratory/lab-store";
import {
  formatLabResultSummary,
  getLabResultComment,
  LAB_STATUS_LABELS,
  type LabOrder,
} from "@/core/laboratory/types";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { routes } from "@/router/all_routes";

export function LabResultsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lineDraft, setLineDraft] = useState<LabResultLineDraft[]>([]);
  const [commentDraft, setCommentDraft] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void syncLabOrdersFromApi().finally(() => {
      if (!cancelled) setRefreshKey((k) => k + 1);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const orders = useMemo(() => {
    void refreshKey;
    return listLabOrders();
  }, [refreshKey]);

  const pending = orders.filter((o) => o.status === "in_progress");
  const completed = orders.filter((o) => o.status === "completed");

  const pendingPagination = useClientPagination(pending, [], 25);
  const { pagedItems: pagedPending } = pendingPagination;

  const completedPagination = useClientPagination(completed, [], 25);
  const { pagedItems: pagedCompleted } = completedPagination;

  const selected = selectedId ? orders.find((o) => o.id === selectedId) ?? null : null;

  const refresh = () => setRefreshKey((k) => k + 1);

  const openOrder = (order: LabOrder) => {
    setSelectedId(order.id);
    setFormError(null);
    setShowPrintPreview(false);
    if (order.resultLines.length > 0) {
      setLineDraft(linesToDraft(order.resultLines));
      setCommentDraft(getLabResultComment(order));
    } else if (order.status !== "completed") {
      setLineDraft(draftFromTestName(order.testName));
      setCommentDraft("");
    } else {
      setLineDraft([]);
      setCommentDraft(order.resultText);
    }
  };

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    try {
      saveLabResult(selected.id, {
        resultLines: lineDraft,
        resultText: commentDraft,
      });
      setFormError(null);
      refresh();
      setShowPrintPreview(true);
    } catch (err) {
      setFormError(
        err instanceof LabOrderValidationError ? err.message : "Enregistrement impossible.",
      );
    }
  };

  const hasStructuredResult = selected && selected.resultLines.length > 0;

  return (
    <div>
      <PageHeader
        title="Résultats laboratoire"
        description="Saisie structurée paramètre / valeur / unité / norme, validation et impression."
        actions={
          <Link to={routes.laboratory} className="btn btn-outline-secondary btn-sm">
            Demandes d&apos;analyses
          </Link>
        }
      />

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-5">
          <div className="ph-invoice-sheet mb-3">
            <div className="ph-invoice-section-title">
              En attente de résultat ({pending.length})
            </div>
            {pending.length === 0 ? (
              <p className="text-muted small mb-0">Aucune analyse en cours.</p>
            ) : (
              <>
              <ul className="list-unstyled mb-0 small">
                {pagedPending.map((order) => (
                  <li key={order.id} className="mb-2 pb-2 border-bottom">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-start"
                      onClick={() => openOrder(order)}
                    >
                      <span className="ph-inv-badge me-1">{order.orderNumber}</span>
                      {order.testName}
                    </button>
                  </li>
                ))}
              </ul>
              <TablePagination
                page={pendingPagination.page}
                pageSize={pendingPagination.pageSize}
                totalItems={pendingPagination.totalItems}
                onPageChange={pendingPagination.setPage}
                onPageSizeChange={pendingPagination.setPageSize}
              />
              </>
            )}
          </div>

          <div className="ph-invoice-sheet">
            <div className="ph-invoice-section-title">
              Résultats validés ({completed.length})
            </div>
            {completed.length === 0 ? (
              <p className="text-muted small mb-0">Aucun résultat enregistré.</p>
            ) : (
              <>
              <ul className="list-unstyled mb-0 small">
                {pagedCompleted.map((order) => {
                  const patient = getPatientById(order.patientId);
                  return (
                    <li key={order.id} className="mb-2 pb-2 border-bottom">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-start"
                        onClick={() => openOrder(order)}
                      >
                        {order.orderNumber} — {patient ? patientFullName(patient) : "—"}
                      </button>
                      <div className="text-muted">{formatLabResultSummary(order)}</div>
                      <div className="text-muted">
                        {order.resultAt
                          ? new Date(order.resultAt).toLocaleString("fr-FR")
                          : "—"}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <TablePagination
                page={completedPagination.page}
                pageSize={completedPagination.pageSize}
                totalItems={completedPagination.totalItems}
                onPageChange={completedPagination.setPage}
                onPageSizeChange={completedPagination.setPageSize}
              />
              </>
            )}
          </div>
        </div>

        <div className="col-lg-7">
          {!selected ? (
            <div className="ph-invoice-sheet">
              <p className="text-muted mb-0">
                Sélectionnez une analyse pour saisir ou consulter le résultat.
              </p>
            </div>
          ) : (
            <div className="ph-invoice-sheet">
              <div className="d-flex justify-content-between align-items-start mb-3 ph-split-detail-head">
                <div>
                  <span className="ph-inv-badge">{selected.orderNumber}</span>
                  <h3 className="h6 fw-semibold mt-2 mb-0">{selected.testName}</h3>
                  <p className="small text-muted mb-0">
                    {getPatientById(selected.patientId)
                      ? patientFullName(getPatientById(selected.patientId)!)
                      : "—"}
                  </p>
                  {selected.prescriber && (
                    <p className="small text-muted mb-0">Prescripteur : {selected.prescriber}</p>
                  )}
                </div>
                <span className={`hosp-diag-badge hosp-diag-badge--${selected.status}`}>
                  {LAB_STATUS_LABELS[selected.status]}
                </span>
              </div>

              {selected.status !== "completed" ? (
                <form onSubmit={handleSave}>
                  <LabResultLinesEditor
                    lines={lineDraft}
                    testName={selected.testName}
                    comment={commentDraft}
                    onLinesChange={setLineDraft}
                    onCommentChange={setCommentDraft}
                  />
                  {formError && (
                    <div className="alert alert-warning py-2 small mt-2">{formError}</div>
                  )}
                  <div className="d-flex gap-2 mt-3 ph-split-actions">
                    <button type="submit" className="btn btn-primary btn-sm">
                      Valider le résultat
                    </button>
                    {selected.status === "requested" && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          updateLabOrderStatus(selected.id, "in_progress");
                          refresh();
                        }}
                      >
                        Passer en cours
                      </button>
                    )}
                  </div>
                </form>
              ) : (
                <>
                  {hasStructuredResult ? (
                    <LabResultLinesEditor
                      lines={linesToDraft(selected.resultLines)}
                      testName={selected.testName}
                      readOnly
                      comment={getLabResultComment(selected)}
                      onLinesChange={() => {}}
                      onCommentChange={() => {}}
                    />
                  ) : (
                    <div
                      className="border rounded p-3 mb-3 bg-light"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {selected.resultText}
                    </div>
                  )}
                  <div className="d-flex gap-2 ph-split-actions">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => printLabResult(selected)}
                    >
                      Imprimer (PDF)
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowPrintPreview((v) => !v)}
                    >
                      Aperçu
                    </button>
                  </div>
                  {showPrintPreview && (
                    <div className="mt-3 border rounded p-2 bg-white">
                      <LabResultPrintSheet order={selected} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
